import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { ampli } from '../../../ampli';
import { MainHead } from '../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useGetProductByIdQuery } from '../../../hooks/api/product/queries/useGetProductById.query';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useScrollY } from '../../../hooks/screen/useScrollY';
import { useCheckUserLoggedIn } from '../../../hooks/user/useCheckUserLoggedIn';
import { postMessageToRN } from '../../../utils/RNCommunicator/postMessageToRN.util';
import { ProductDetailView } from '../../../views/Product/detailView/ProductDetailView/ProductDetailView';
import { InstallmentPossibleSection } from './components/InstallmentPossibleSection';
import { ProductDetailPageFooter } from './components/ProductDetailPageFooter';
import { RequestConsultButton } from './components/RequestConsultButton';

const MAIN_HEAD_HEIGHT = 40;
const INSTALLMENT_POSSIBLE_SECTION_HEIGHT = 36.8;

export const ProductDetailPage = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const [isAmpliEventCalled, setIsAmpliEventCalled] = useState<boolean>(false);

  const [isRequestConsultModalClosed, setIsRequestConsultModalClosed] = useState<boolean>(false);

  const { scrollY } = useScrollY();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const { data: product, isLoading: isGetProductQueryLoading } = useGetProductByIdQuery({
    productId: productId!,
  });

  const isRequestConsultModalOpenTrigged = useMemo<boolean>(() => {
    if (window.pageYOffset > 2000) {
      return true;
    }

    return false;
  }, [window.pageYOffset, scrollY]);

  const isOpenRequestConsultModal = useMemo<boolean>(() => {
    return !isRequestConsultModalClosed && isRequestConsultModalOpenTrigged;
  }, [isRequestConsultModalClosed, isRequestConsultModalOpenTrigged]);

  const handleRequestConsultButtonClick = () => {
    if (product) {
      if (product.category === 'PLUVA') {
        ampli.마켓플루바매물상담권유버튼클릭({
          '게시글 제목': product?.title ?? '',
          '매물 코드': product?.code,
        });
      } else if (product.category === 'PARTNER') {
        ampli.마켓파트너매물상담권유버튼클릭({
          '게시글 제목': product?.title ?? '',
          '매물 코드': product?.code,
        });
      }
    }

    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'link', messageParam: 'tel:0316074997' });
    } else {
      window.location.href = 'tel:0316074997';
    }
  };

  useEffect(() => {
    if (product) {
      if (product.status !== 'ONSALE' && product.status !== 'IN_CONTRACT') {
        navigate('/');
      }
    }
  }, [product, navigate]);

  useEffect(() => {
    if (product && !isAmpliEventCalled) {
      if (product.category === 'PLUVA') {
        ampli.마켓플루바매물상세페이지조회({
          '게시글 제목': product.title,
          '매물 코드': product.code,
        });
      } else {
        ampli.마켓파트너매물상세페이지조회({
          '게시글 제목': product.title,
          '매물 코드': product.code,
        });
      }

      setIsAmpliEventCalled(true);
    }
  }, [product, isAmpliEventCalled]);

  useLayout({
    fixedHeader: (
      <>
        <MainHead backButton={{ isShow: true, directlyPassedURL: pageInfo.productListPage.path }} />
        <InstallmentPossibleSection />
      </>
    ),
    fixedFooter: (
      <ProductDetailPageFooter>
        <RequestConsultButton product={product!} category={product?.category === 'PLUVA' ? 'PLUVA' : 'PARTNER'} />
      </ProductDetailPageFooter>
    ),
    fixedHeaderHeight: MAIN_HEAD_HEIGHT + INSTALLMENT_POSSIBLE_SECTION_HEIGHT,
    fixedFooterHeight: 0,
  });

  if (isGetProductQueryLoading) return <AnimateLoading />;

  if (product) {
    return (
      <>
        <Helmet>
          <title>{`${product.title} - Pluva Market`}</title>
        </Helmet>

        <ProductDetailView product={product} category={product.category === 'PLUVA' ? 'PLUVA' : 'PARTNER'} />

        <PopupConfirmModal
          isShow={isOpenRequestConsultModal}
          hasCloseButton={false}
          title={'가격 · 할부 조건이 궁금하세요?'}
          description={'가격이나 매물 상태 등\n가벼운 문의도 괜찮아요.'}
          closeModal={() => setIsRequestConsultModalClosed(true)}
          buttonArray={[
            {
              text: '전화로 물어보기',
              onClick: handleRequestConsultButtonClick,
              color: 'blue',
            },
            {
              text: '닫기',
              onClick: () => setIsRequestConsultModalClosed(true),
              color: 'gray',
            },
          ]}
        />
      </>
    );
  }

  return <></>;
};
